// Asset upload service for experiment images

import { getApiBaseUrl } from '@/config/domains'

/**
 * Get CDN URL for uploaded asset
 * @param s3Key - S3 key from upload response
 * @returns CDN URL for serving the asset
 */
export function getAssetCDNUrl(s3Key: string): string {
  // This will be replaced with the actual CDN domain from config
  const CDN_BASE_URL = import.meta.env.VITE_ASSETS_CDN_URL || 'https://assets.dev.tn-xp.eclab.pro'
  
  // S3 key format: stores/{store_id}/hero-banners/{experiment_id}/desktop-{asset_id}.webp
  // CDN URL: https://assets.dev.tn-xp.eclab.pro/{s3_key}
  return `${CDN_BASE_URL}/${s3Key}`
}

/**
 * Upload a single image to staging path (used by visual editor templates).
 * Uses presigned URL: Lambda returns a signed S3 PUT URL, then we upload directly.
 * Staging uploads auto-expire after 24h; promoted to permanent on experiment creation.
 */
export async function uploadStagingAsset(
  storeId: string,
  uploadSessionId: string,
  file: File,
  imageType: 'desktop_image' | 'mobile_image' = 'desktop_image',
  apiBaseUrlOverride?: string
): Promise<{ asset_id: string; s3_key: string }> {
  // Fail fast on a missing session id: without it, the backend would silently
  // file the upload under stores/<store>/hero-banners/staging/… (an unintended
  // path that escapes the 24-h staging-prefix lifecycle and leaks forever).
  // The editor should always carry uploadSessionId via editorUIState.
  if (!uploadSessionId) {
    throw new Error('Missing upload_session_id — relaunch the editor from the experiment wizard.')
  }

  // Resolve the API host the same way the rest of the app does (hostname-based,
  // via domainManager) — NOT window.location.origin. In the popped-out editor
  // the origin is the frontend host (dev.tn-xp.eclab.pro), and POSTing there
  // hits the static site → 405. getApiBaseUrl() always yields api.<env>.<base>.
  const apiBaseUrl = apiBaseUrlOverride || getApiBaseUrl()

  // Step 1: Get presigned URL from Lambda
  const response = await fetch(
    `${apiBaseUrl}/api/experiments/staging/assets`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Store-ID': storeId,
        'X-Upload-Session-Id': uploadSessionId,
      },
      body: JSON.stringify({
        filename: file.name,
        image_type: imageType,
      }),
    }
  )

  if (!response.ok) {
    const errorData = await response.json()
    throw new Error(errorData.error?.message || 'Failed to get upload URL')
  }

  const result = await response.json()
  const { presigned_url, asset_id, s3_key, content_type } = result.data
  if (!presigned_url) {
    throw new Error('No presigned URL returned')
  }

  // Step 2: Upload file directly to S3
  const uploadResponse = await fetch(presigned_url, {
    method: 'PUT',
    headers: { 'Content-Type': content_type },
    body: file,
  })

  if (!uploadResponse.ok) {
    throw new Error(`S3 upload failed: ${uploadResponse.status}`)
  }

  return { asset_id, s3_key }
}

/**
 * Get staging CDN URL for preview during editor session
 */
export function getStagingPreviewUrl(s3Key: string): string {
  const CDN_BASE_URL = import.meta.env.VITE_ASSETS_CDN_URL || 'https://assets.dev.tn-xp.eclab.pro'
  return `${CDN_BASE_URL}/${s3Key}`
}

export const assetsService = {
  uploadStagingAsset,
  getAssetCDNUrl,
  getStagingPreviewUrl,
}